import { format } from 'date-fns'
import {
  AlertCircle,
  Database,
  ExternalLink
} from 'lucide-react'
import React, { useState } from 'react'

import { Badge, Button } from 'slab'

import { type IngestionJob } from './types'

export const ExpandedDataIngestionRow: React.FC<{ job: IngestionJob }> = ({ job }) => {
  const [showFullError, setShowFullError] = useState<Record<string, boolean>>({})
  const [showDataSummary, setShowDataSummary] = useState<Record<string, boolean>>({})

  const hasFailures = job.failed_count > 0
  const borderColor = hasFailures ? 'border-orange-400' : 'border-blue-400'

  return (
    <div className={`p-6 bg-gray-100 border-l-4 ${borderColor}`}>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h4 className='font-semibold text-gray-900 flex items-center gap-2'>
            <Database className='w-5 h-5 text-blue-600' />
            State Ingestion Details
          </h4>
          {hasFailures && (
            <div className='mt-1 flex items-center gap-2 text-sm'>
              <AlertCircle className='w-4 h-4 text-orange-600' />
              <span className='text-orange-700'>
                {job.failed_count} of {job.total_states} state(s) failed. Expand failed states below for error details.
              </span>
            </div>
          )}
        </div>
        {job.good_job_url && (
          <Button
            size='small'
            variant='tertiary'
            leftIcon={ExternalLink}
            onClick={() => window.open(job.good_job_url!, '_blank')}
          >
            View in GoodJob
          </Button>
        )}
      </div>
      <div className='space-y-2 max-h-96 overflow-y-auto'>
        {job.ingestions.map((ingestion) => (
          <div
            key={ingestion.id}
            className='bg-white p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors'
          >
            <div className='flex items-start justify-between'>
              <div className='flex-1'>
                <div className='flex items-center gap-3 mb-2'>
                  <span className='font-medium text-gray-900'>
                    {ingestion.state_name} ({ingestion.state_code})
                  </span>
                  <StateStatusBadge status={ingestion.status} />
                  {ingestion.rolled_back && (
                    <Badge type='warning'>Rolled Back</Badge>
                  )}
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 text-sm text-gray-600'>
                  <div>
                    <span className='font-medium'>Started:</span>{' '}
                    {ingestion.started_at
                      ? format(new Date(ingestion.started_at), 'MMM d, yyyy HH:mm:ss')
                      : 'Not started'}
                  </div>
                  <div>
                    <span className='font-medium'>Completed:</span>{' '}
                    {ingestion.completed_at
                      ? format(new Date(ingestion.completed_at), 'MMM d, yyyy HH:mm:ss')
                      : 'Not completed'}
                  </div>
                  <div>
                    <span className='font-medium'>Duration:</span>{' '}
                    {ingestion.duration_seconds
                      ? `${Math.round(ingestion.duration_seconds)} seconds`
                      : 'N/A'}
                  </div>
                </div>

                {ingestion.data_summary && (
                  <div className='mt-2 p-2 bg-blue-50 rounded max-w-full'>
                    <div className='flex items-start justify-between'>
                      <span className='font-medium text-blue-900 text-sm'>Data Summary</span>
                      <Button
                        size='small'
                        variant='tertiary-deemphasized'
                        onClick={() =>
                          setShowDataSummary((prev) => ({
                            ...prev,
                            [ingestion.id]: !prev[ingestion.id],
                          }))}
                      >
                        {showDataSummary[ingestion.id] ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showDataSummary[ingestion.id] && (
                      <pre className='mt-2 text-xs text-blue-800 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap'>
                        {JSON.stringify(ingestion.data_summary, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                {ingestion.error_details && (
                  <div className='mt-2 p-2 bg-red-50 rounded max-w-full'>
                    <div className='flex items-start justify-between'>
                      <span className='font-medium text-red-900 text-sm'>Error:</span>
                      <Button
                        size='small'
                        variant='tertiary-deemphasized'
                        onClick={() =>
                          setShowFullError((prev) => ({
                            ...prev,
                            [ingestion.id]: !prev[ingestion.id],
                          }))}
                      >
                        {showFullError[ingestion.id] ? 'Show less' : 'Show more'}
                      </Button>
                    </div>
                    <div className='mt-1 text-xs text-red-800 break-words'>
                      <div className='break-words'>{ingestion.error_details.message}</div>
                      {ingestion.error_details.class && (
                        <div className='text-red-600 mt-1'>
                          Class: {ingestion.error_details.class}
                        </div>
                      )}
                      {showFullError[ingestion.id] && ingestion.error_details.backtrace && (
                        <pre className='mt-2 overflow-x-auto text-xs bg-red-100 p-2 rounded max-h-48 overflow-y-auto whitespace-pre-wrap break-all'>
                          {ingestion.error_details.backtrace.join('\n')}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const StateStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  let type: React.ComponentProps<typeof Badge>['type'] = 'neutral'

  switch (status) {
    case 'pending':
      type = 'slate'
      break
    case 'processing':
      type = 'caution'
      break
    case 'success':
      type = 'success'
      break
    case 'failed':
      type = 'critical'
      break
    case 'cancelled':
      type = 'warning'
      break
  }

  return <Badge type={type}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>
}
